'use client';

import { useEffect, useState } from 'react';
import { Laboratory } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import {
  buildLaboratoryCartItems,
  getUnpricedExams,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/_utils/laboratory-cart-items.util';
import { LaboratoryErrorFallback } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-error-fallback.component';
import { getLaboratoryScheduleFromSearchApi } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-schedule-from-search-api.service';
import { ArrivalOrderModalContent } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-modal.component';
import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { SearchApiFacilityExam } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { LaboratoryExamsFlowKeys } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-exams-flow/laboratory-exams-flow.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';
import { ADD_TO_CART_ERROR_MESSAGE, useAddToCart } from '@/hooks/use-add-to-cart.hook';
import { useCartAdditionConfirmation } from '@/hooks/use-cart-addition-confirmation.hook';
import { getLogger } from '@/logger/logger';
import { PatientOption } from '@/types/patient-selection.types';
import { isSchedulePast } from '@/utils/schedule/is-schedule-past.util';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';

const logger = getLogger('laboratory.schedules-step');

export const UNAVAILABLE_SLOT_MESSAGE = 'Esse horário não está mais disponível.';

interface LaboratorySchedulesStepProps {
  laboratory: Laboratory;
  exams: SearchApiFacilityExam[];
  total: number;
}

export function LaboratorySchedulesStep({
  laboratory,
  exams,
  total,
}: LaboratorySchedulesStepProps) {
  const facilityId = laboratory.id ?? '';
  const procedureIds = exams.map((exam) => exam.id);
  const procedureIdsKey = JSON.stringify(procedureIds);
  const { requestPatientSelection } = usePatientSelection();
  const { onClose } = useProgressiveModal<LaboratoryExamsFlowKeys>();
  const { addToCart } = useAddToCart();
  const { confirmCartAddition } = useCartAdditionConfirmation();
  const { toast } = useToast();

  const [scheduleId, setScheduleId] = useState<string | undefined>();
  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(Boolean(facilityId));
  const [hasScheduleError, setHasScheduleError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!facilityId) {
      return;
    }

    let cancelled = false;

    setIsLoadingSchedule(true);
    setScheduleId(undefined);
    setPartnerId(undefined);
    setHasScheduleError(false);

    const procedureIds = JSON.parse(procedureIdsKey) as string[];

    getLaboratoryScheduleFromSearchApi({ facilityId, procedureIds })
      .then((result) => {
        if (!cancelled) {
          setScheduleId(result.scheduleId ?? undefined);
          setPartnerId(result.partnerId ?? undefined);
        }
      })
      .catch((error) => {
        logger.error('Failed to resolve the laboratory schedule', {
          laboratory: {
            event: 'schedule_resolution_failed',
            facility_id: facilityId,
            procedure_ids: procedureIds,
          },
          error,
        });

        if (!cancelled) {
          setHasScheduleError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSchedule(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, procedureIdsKey, reloadToken]);

  const refuseExpiredSlot = (slot: SchedulesSearchSlot): boolean => {
    if (!isSchedulePast(slot.end)) {
      return false;
    }

    toast.error(UNAVAILABLE_SLOT_MESSAGE);

    return true;
  };

  const handleSelectPatient = async (slot: SchedulesSearchSlot, patient: PatientOption) => {
    if (refuseExpiredSlot(slot)) {
      return;
    }

    const unpricedExams = getUnpricedExams(exams);

    if (unpricedExams.length > 0) {
      logger.error('Blocked a purchase without a procedure price', {
        cart: {
          event: 'unpriced_procedure',
          stage: 'price_guard',
          target: 'cart',
          partner_id: partnerId,
          partner_facility_id: facilityId,
          procedure_ids: unpricedExams.map((exam) => exam.id),
          slot_id: slot.id,
        },
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return;
    }

    await addToCart(buildLaboratoryCartItems(exams, patient.id, facilityId, slot.id));
  };

  const handleSelectSlot = (slot: SchedulesSearchSlot) => {
    requestPatientSelection({
      onBeforeSelection: async () => {
        if (refuseExpiredSlot(slot)) {
          return false;
        }

        return confirmCartAddition(
          {
            partnerId: partnerId ?? '',
            facility: {
              id: facilityId,
              city: laboratory.city ?? undefined,
              state: laboratory.state ?? undefined,
            },
            isOnline: false,
          },
          ScheduleType.LABORATORIAL,
          { isStandalone: false },
        );
      },
      onSelectPatient: (patient) => handleSelectPatient(slot, patient),
    });

    onClose();
  };

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL}
    >
      <ArrivalOrderModalContent
        scheduleIds={scheduleId ? [scheduleId] : []}
        entityName={resolveLaboratoryName(laboratory)}
        entityAvatar={laboratory.main?.avatar}
        entityType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
        partnerId={partnerId ?? ''}
        price={total}
        slotFilters={{}}
        isLoadingSchedule={isLoadingSchedule}
        onSelectSlot={handleSelectSlot}
        errorContent={hasScheduleError
          ? (
              <LaboratoryErrorFallback
                message="Não foi possível carregar os horários."
                laboratoryId={facilityId}
                dataTestId={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL_ERROR}
                onRetry={() => setReloadToken((current) => current + 1)}
              />
            )
          : undefined}
      />
    </div>
  );
}
